import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { siteInfo } from "@/mocks/site";
import { categories } from "@/mocks/categories";

const values = [
  {
    icon: "ri-check-double-line",
    title: "内容为尺",
    desc: "我们严格筛选剧情扎实、逻辑严密、反转到位的悬疑推理作品，让每一部都值得细读。",
  },
  {
    icon: "ri-hand-heart-line",
    title: "读者为先",
    desc: "从分类到追更，一切设计都以推理爱好者的阅读体验为出发点，追更更省心。",
  },
  {
    icon: "ri-fire-line",
    title: "持续上新",
    desc: "每周稳定上新多部佳作，连载作品持续更新，为你带来源源不断的烧脑内容。",
  },
  {
    icon: "ri-shield-check-line",
    title: "理性阅读",
    desc: "站内作品均为虚构故事，我们倡导理性看待剧情与现实，营造健康阅读氛围。",
  },
];

const stats = [
  { num: "12+", label: "在更优质作品" },
  { num: "6", label: "主题分类" },
  { num: "2000+", label: "累计章节" },
  { num: "120w+", label: "推理读者" },
];

export default function About() {
  useSeo({
    title: "关于我们_汗汗漫画悬疑推理追更平台",
    description:
      "了解汗汗漫画的品牌定位与发展理念。汗汗漫画是专注悬疑、推理、烧脑、反转、案件、解密漫画的在线阅读追更平台，致力于为推理爱好者提供优质内容。",
    keywords: "汗汗漫画,关于汗汗漫画,悬疑推理漫画平台,汗汗漫画介绍",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `关于${siteInfo.name}`,
        url: `${siteInfo.url}/about`,
        about: {
          "@type": "Organization",
          name: siteInfo.name,
          description: siteInfo.description,
          email: siteInfo.email,
        },
      },
    ],
  });

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-16">
      <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
        <Link to="/" className="cursor-pointer transition-colors hover:text-accent-300">首页</Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="font-medium text-accent-300">关于我们</span>
      </nav>

      {/* 品牌介绍 */}
      <section className="relative mt-6 overflow-hidden rounded-lg border border-background-200/60 bg-background-100">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl"></div>
        <div className="relative z-10 grid grid-cols-1 gap-8 p-6 md:p-12 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1 rounded-full border border-accent-300/40 bg-accent-500/10 px-4 py-1.5 text-xs font-medium text-accent-200">
              <i className="ri-sparkling-line" aria-hidden="true"></i>
              关于我们
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black text-foreground-50 md:text-4xl">
              让每个烧脑真相
              <span className="mystery-text"> 都有处可追</span>
            </h1>
            <p className="mt-5 text-sm leading-relaxed text-foreground-400 md:text-base">
              {siteInfo.name}是专注悬疑推理题材的漫画在线阅读与追更平台。我们相信，一部好的推理作品，
              能让人在反转与真相之间获得纯粹的智力乐趣。为此，我们精心筛选并持续更新
              悬疑、推理、烧脑、反转、案件、解密六大类型佳作。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500">
              无论你是偏爱层层铺垫的悬疑，还是热衷严丝合缝的推理，都能在这里找到合口味的故事，
              与千万推理爱好者一起追更烧脑反转的精彩剧情。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 self-center">
            {stats.map((s) => (
              <div
                key={s.label}
                className="rounded-lg border border-background-200/60 bg-background-900/40 p-6 text-center"
              >
                <div className="font-heading text-3xl font-black text-accent-300">
                  {s.num}
                </div>
                <div className="mt-1 text-sm text-foreground-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="mt-12">
        <h2 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
          我们的内容原则
        </h2>
        <p className="mt-2 text-sm text-foreground-500">
          平台定位与选品标准，始终以读者价值为先
        </p>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v) => (
            <div
              key={v.title}
              className="rounded-lg border border-background-200/60 bg-background-100 p-6 transition-colors hover:border-accent-300/50"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
                <i className={`${v.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-100">
                {v.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                {v.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 分类覆盖 */}
      <section className="mt-12 rounded-lg border border-background-200/60 bg-background-100 p-6 md:p-8">
        <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
          覆盖六大推理类型
        </h2>
        <div className="mt-6 flex flex-wrap gap-3">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group flex cursor-pointer items-center gap-2 rounded-md border border-background-200/60 bg-background-900/40 px-4 py-2.5 text-sm text-foreground-300 transition-colors hover:border-accent-300/50 hover:text-accent-200"
            >
              <i className={`${c.icon} text-primary-400`} aria-hidden="true"></i>
              {c.name}漫画
              <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
            </Link>
          ))}
        </div>
      </section>

      {/* 联系方式 */}
      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
          联系我们
        </h2>
        <p className="mt-2 text-sm text-foreground-500">
          有任何建议、推荐作品或合作意向，欢迎与我们联系
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-4 rounded-lg border border-background-200/60 bg-background-100 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
              <i className="ri-mail-line text-xl" aria-hidden="true"></i>
            </span>
            <div>
              <p className="text-xs text-foreground-500">电子邮箱</p>
              <p className="text-sm font-medium text-foreground-100">{siteInfo.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-lg border border-background-200/60 bg-background-100 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500/15 text-accent-300">
              <i className="ri-article-line text-xl" aria-hidden="true"></i>
            </span>
            <div>
              <p className="text-xs text-foreground-500">想推荐作品？</p>
              <p className="text-sm font-medium text-foreground-100">欢迎留言告诉我们你的心头好</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}